import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, RefreshCw, Inbox } from "lucide-react";
import AdminOrderCard from "@/components/admin/AdminOrderCard";
import AdminReviews from "@/components/admin/AdminReviews";
import { fetchOrders, fetchReviews, clearToken, apiError, STATUSES } from "@/lib/admin";

export default function AdminInbox({ onLogout }) {
    const [orders, setOrders] = useState(null);
    const [filter, setFilter] = useState("all");
    const [error, setError] = useState("");
    const [tab, setTab] = useState("orders");
    const [pendingReviews, setPendingReviews] = useState(0);

    const load = async () => {
        setError("");
        try {
            setOrders(await fetchOrders());
            fetchReviews().then((r) => setPendingReviews(r.filter((x) => x.status === "pending").length)).catch(() => {});
        } catch (e) {
            if (e?.response?.status === 401) return onLogout();
            setError(apiError(e));
        }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const replace = (updated) => setOrders((list) => list.map((o) => (o.id === updated.id ? updated : o)));
    const visible = (orders || []).filter((o) => filter === "all" || o.status === filter);
    const count = (s) => (orders || []).filter((o) => o.status === s).length;

    return (
        <div className="min-h-screen bg-[#F7F2E9]" data-testid="admin-inbox">
            <header className="sticky top-0 z-40 border-b border-[#F2E1E6] bg-[#FFFDF9]/90 backdrop-blur-xl">
                <div className="max-w-5xl mx-auto flex items-center justify-between px-4 sm:px-6 h-20">
                    <Link to="/" className="flex items-center gap-3" data-testid="admin-logo">
                        <img src="/annabells-logo.png" alt="Annabells Cupcakes and More" className="h-12 w-12 rounded-full object-cover ring-2 ring-[#D4AF37]/50" />
                        <span className="leading-none">
                            <span className="font-script text-2xl text-[#C95B7D] block">Annabells</span>
                            <span className="text-[10px] tracking-[0.3em] uppercase text-[#8C6A5D] font-semibold">Order Inbox</span>
                        </span>
                    </Link>
                    <div className="flex items-center gap-2">
                        <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 rounded-full border border-[#3D2318]/15 bg-white/70 px-4 py-2 text-xs font-semibold text-[#3D2318] hover:bg-[#FAF0D7]/70 transition-colors">
                            <RefreshCw className="h-3.5 w-3.5" /> Refresh
                        </button>
                        <button onClick={() => { clearToken(); onLogout(); }} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full bg-[#3D2318] px-4 py-2 text-xs font-semibold text-[#F8D7E3] hover:bg-[#C95B7D] hover:text-white transition-colors">
                            <LogOut className="h-3.5 w-3.5" /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
                <div className="mb-8 inline-flex rounded-full border border-[#F2E1E6] bg-white p-1" data-testid="admin-tabs">
                    {[{ id: "orders", label: "Orders" }, { id: "reviews", label: "Reviews" }].map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`admin-tab-${t.id}`}
                            className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === t.id ? "bg-[#3D2318] text-[#F8D7E3]" : "text-[#6B4A3E] hover:text-[#C95B7D]"}`}
                        >
                            {t.label}
                            {t.id === "reviews" && pendingReviews > 0 && (
                                <span className="rounded-full bg-[#C95B7D] px-2 py-0.5 text-[10px] font-bold text-white" data-testid="admin-pending-reviews-badge">{pendingReviews}</span>
                            )}
                        </button>
                    ))}
                </div>

                {tab === "reviews" ? (
                    <AdminReviews onLogout={onLogout} onCountChange={setPendingReviews} />
                ) : (
                <>
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27]">Order Requests</p>
                        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold text-[#3D2318]">
                            {orders ? `${orders.length} request${orders.length === 1 ? "" : "s"}` : "Loading…"}
                        </h1>
                    </div>
                    <div className="flex flex-wrap gap-2" data-testid="admin-filters">
                        {[{ value: "all", label: "All" }, ...STATUSES].map((s) => (
                            <button
                                key={s.value}
                                onClick={() => setFilter(s.value)}
                                data-testid={`admin-filter-${s.value}`}
                                className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${filter === s.value ? "bg-[#3D2318] text-[#F8D7E3] border-[#3D2318]" : "bg-white text-[#6B4A3E] border-[#F2E1E6] hover:border-[#C95B7D]"}`}
                            >
                                {s.label}{s.value !== "all" && orders ? ` · ${count(s.value)}` : ""}
                            </button>
                        ))}
                    </div>
                </div>

                {error && <p className="mt-6 rounded-2xl bg-[#fdecea] px-4 py-3 text-sm text-[#b3261e]" data-testid="admin-error">{error}</p>}

                {orders && visible.length === 0 && (
                    <div className="mt-12 flex flex-col items-center rounded-[2rem] border border-dashed border-[#F2E1E6] bg-white/60 p-12 text-center" data-testid="admin-empty">
                        <Inbox className="h-10 w-10 text-[#C95B7D]" />
                        <p className="mt-4 font-display text-xl font-bold text-[#3D2318]">Nothing here yet</p>
                        <p className="mt-1 text-sm text-[#6B4A3E]">New order requests will show up here as soon as customers send them.</p>
                    </div>
                )}

                <div className="mt-8 grid gap-5" data-testid="admin-order-list">
                    {visible.map((o) => <AdminOrderCard key={o.id} order={o} onChange={replace} />)}
                </div>
                </>
                )}
            </main>
        </div>
    );
}
